import React, { useEffect, useState, useMemo } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import DepartmentsPicker from 'views/Users/common/DepartmentsPicker';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/detailsStyle';
import SelectAccess from 'views/Users/Details/UserDetails/dialogs/subcomponents/SelectAccess';
import DepartmentBadges from 'views/Users/Details/UserDetails/dialogs/subcomponents/DepartmentBadges';
import _ from 'lodash';
import SimpleTable from 'components/SimpleTable/SimpleTable';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { BodyS } from 'components/Typography/Typography';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { MAX_ADMIN_COUNT } from 'utils/const/users';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IAddPermissionProps {
	hideDialog: () => void;
	accounts: types.users.ISingleAccount[];
	departments: SingleDepartment[];
	isEnterprise: boolean;
	isManager: boolean;
	isAdmin: boolean;
	isCorporate: boolean;
	handleSaveData: (permissions: types.users.IExtendedSinglePermission[]) => void;
}

const AddPermission = ({
	hideDialog,
	accounts,
	departments,
	isEnterprise,
	isManager,
	isCorporate,
	isAdmin,
	handleSaveData,
}: IAddPermissionProps) => {
	const [selectedEnterpriseDepartments, setSelectedEnterpriseDepartments] = useState<string[]>([]);
	const [selectedCorporateDepartments, setSelectedCorporateDepartments] = useState<{ [key: string]: any }>({});
	const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
	const [date, setDate] = useState<{ isUnlimited: boolean; date: string }>({ isUnlimited: false, date: '' });
	const [showIsValid, setShowIsValid] = useState<boolean>(false);
	const classes = useStyles();

	const resetData = () => {
		setSelectedEnterpriseDepartments([]);
		setDate({ isUnlimited: true, date: '' });
		setSelectedCorporateDepartments({});
		setSelectedAccounts([]);
		setShowIsValid(false);
	};

	useEffect(() => {
		resetData();
	}, []);

	let content = null;

	const corporateDepartmentsCheck = (() => {
		const isChecked = (deptId?: string): boolean => !!deptId && deptId in selectedCorporateDepartments;
		const getValue = (deptId?: string) => !!deptId && selectedCorporateDepartments[deptId];

		return {
			isChecked,
			getValue,
			isValid: (deptId: string) =>
				isChecked(deptId) && (getValue(deptId).isUnlimited || getValue(deptId).permissionExpiration !== ''),
		};
	})();

	const isFormValid = useMemo(() => {
		if (isCorporate && isManager) {
			return Object.keys(selectedCorporateDepartments).every((departmentId) =>
				corporateDepartmentsCheck.isValid(departmentId)
			);
		} else {
			return date.date !== '' || date.isUnlimited;
		}
	}, [isCorporate, isManager, date, selectedCorporateDepartments, corporateDepartmentsCheck]);

	const preparedData: types.users.IExtendedSinglePermission[] = useMemo(() => {
		const expiration = date.isUnlimited ? undefined : date.date;

		const prepareDepartment = (deptId: string): types.users.IPreparedDepartment => {
			const department = departments.find((department) => deptId === department.deptId);
			return {
				departmentId: department!.deptId,
				departmentName: department!.name,
				departmentUiColor: department!.uiColor,
				accountId: department!.accountId,
			};
		};

		if (isEnterprise) {
			if (isManager) {
				return selectedEnterpriseDepartments.map((departmentId) => ({
					departmentId,
					type: 'D',
					permissionExpiration: expiration,
					...prepareDepartment(departmentId),
				}));
			} else if (isAdmin) {
				return selectedAccounts.map((accountId) => ({
					accountId,
					accountName: accounts.find(({ id }) => id === accountId)?.name || '',
					type: 'A',
					permissionExpiration: expiration,
				}));
			}
		} else if (isCorporate) {
			if (isManager) {
				return Object.values(selectedCorporateDepartments).map((data) => {
					return {
						permissionExpiration: data.isUnlimited ? '' : data.permissionExpiration,
						type: 'D',
						...prepareDepartment(data.deptId),
					};
				});
			} else if (isAdmin) {
				return [
					{
						permissionExpiration: expiration,
						accountName: accounts[0].name,
						type: 'A',
						accountId: accounts[0].id,
					},
				];
			}
		}
		return [];
	}, [
		isEnterprise,
		isManager,
		isCorporate,
		selectedEnterpriseDepartments,
		date,
		selectedCorporateDepartments,
		accounts,
		isAdmin,
		selectedAccounts,
		departments,
	]);

	const preparedAccounts = accounts.map((account) => ({
		...account,
		disabled: account.adminsCount >= MAX_ADMIN_COUNT,
	}));

	const someAccountsDisabled = preparedAccounts.some(({ disabled }) => disabled);
	const allAccountsDisabled = preparedAccounts.every(({ disabled }) => disabled);

	if (isEnterprise) {
		if (isManager) {
			content = (
				<DepartmentsPicker
					accounts={accounts}
					departments={departments}
					selectedDepartments={selectedEnterpriseDepartments}
					setSelectedDepartments={setSelectedEnterpriseDepartments}
				/>
			);
		} else if (isAdmin) {
			content = (
				<>
					<ConditionalRender show={allAccountsDisabled}>
						<div className={classes.maxAmount}>
							<BodyS>{translate('users.details.maxAmountAdmin')}</BodyS>
						</div>
					</ConditionalRender>
					<ConditionalRender show={!allAccountsDisabled}>
						<SimpleTable
							withCheckboxes
							idAccessor="id"
							selectedItems={selectedAccounts}
							setSelectedItems={setSelectedAccounts}
							columns={[
								{
									accessor: 'name',
									cell: (row: any) => (
										<>
											<BodyS disableMargin inline>
												{row.name}
											</BodyS>
											<ConditionalRender show={row.disabled}>
												<BodyS disableMargin inline>
													*
												</BodyS>
											</ConditionalRender>
										</>
									),
									header: translate('organization.name'),
								},
							]}
							data={preparedAccounts}
						/>
					</ConditionalRender>
					<ConditionalRender show={someAccountsDisabled && !allAccountsDisabled}>
						<div className={classes.maxReached}>
							<BodyS color="secondary-light">{translate('add.user.max.amount.reached')}</BodyS>
						</div>
					</ConditionalRender>
				</>
			);
		}
	} else if (isCorporate) {
		if (isManager) {
			const { isChecked, getValue, isValid } = corporateDepartmentsCheck;
			content = (
				<DepartmentBadges
					departments={departments}
					isDepartmentValid={isValid}
					showIsValid={showIsValid}
					handleIsChecked={isChecked}
					handleIsUnlimited={(deptId?) => isChecked(deptId) && getValue(deptId).isUnlimited}
					handleGetDate={(deptId) => (isChecked(deptId) ? getValue(deptId).permissionExpiration : '')}
					handleSetDate={(deptId, value) => {
						setSelectedCorporateDepartments((departments) => {
							const newValue = _.cloneDeep(departments);
							if (value === 'unlimited') {
								newValue[deptId] = {
									...newValue[deptId],
									isUnlimited: !newValue[deptId].isUnlimited,
									permissionExpiration: '',
								};
							} else {
								newValue[deptId] = {
									...newValue[deptId],
									isUnlimited: false,
									permissionExpiration: value,
								};
							}
							return newValue;
						});
					}}
					handleBadgeClick={(deptId) => {
						setSelectedCorporateDepartments((departments) => {
							const newValue = _.cloneDeep(departments);
							if (departments[deptId]) {
								delete newValue[deptId];
							} else {
								newValue[deptId] = {
									isUnlimited: true,
									deptId,
								};
							}
							return newValue;
						});
					}}
				/>
			);
		}
	}

	return (
		<CustomDialog
			isOpen
			hideDialog={() => hideDialog()}
			header={translate('users.details.addPermissions')}
			dialogBody={
				<div className={classes.dialogBody}>
					<ConditionalRender show={!allAccountsDisabled && (isEnterprise || (isCorporate && isAdmin))}>
						<SelectAccess
							isUnlimited={date.isUnlimited}
							date={date.date}
							setDate={(value: string) =>
								setDate((date) => {
									if (value === 'unlimited') {
										return {
											isUnlimited: !date.isUnlimited,
											date: '',
										};
									} else {
										return {
											isUnlimited: false,
											date: value,
										};
									}
								})
							}
						/>
					</ConditionalRender>
					{content}
				</div>
			}
			rightSideButtons={[
				{
					text: translate('cancel'),
					color: 'tertiary-light',
					onClick: () => {
						resetData();
						hideDialog();
					},
				},
				{
					text: translate('add'),
					color: 'primary-light',
					disabled: preparedData?.length === 0,
					onClick: () => {
						isFormValid && handleSaveData(preparedData);
						setShowIsValid(true);
						resetData();
					},
				},
			]}
		/>
	);
};

export default AddPermission;
